// The Contacts panel (Session ASN, Phase 9, Director decision D86), shown on the customer page and on the site page.
//
// Named people on an account, each with a role (billing, on-site / facilities, managing agent, who signs, emergency /
// keyholder, or other in the office's own words), an email and a phone, and optionally tied to one of the customer's
// sites. A managing agent that is itself an ELE customer is LINKED to that customer's own record (the name shown is
// read live from it), so the office can click straight through to the agent's account.
//
// On the CUSTOMER page it lists every contact on the account (site ones say which site), and underneath, the accounts
// this customer acts as managing agent for. On the SITE page it lists the people at that building, and a contact added
// there is tied to it.
//
// RULE 1: Remove ARCHIVES (reversible: "Show archived" then Restore). "Delete permanently" is offered only on an
// archived contact, behind the two-step red confirm, for a person who asks for their details to be erased.
//
// A RECORD FOR THE OFFICE ONLY. The panel says so in one line: nothing is emailed to these people automatically, and
// the billing email and secondary contact above still decide who the platform emails.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { sites as sitesResource } from "../../lib/resources";
import { RecordPicker } from "../../components/RecordPicker";
import { CONTACT_ROLE_LABEL, CONTACT_ROLES, CORRESPONDENT_ROLES, contactsApi, type Contact, type ContactRole } from "../../lib/contacts";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { formatDateLong } from "../../lib/format";
import { ContactValue } from "../../components/ContactValue";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs tracking-wide text-ele-body";
const smallBtn =
  "rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50";

interface Option {
  id: string;
  name: string;
}

interface FormState {
  name: string;
  role: ContactRole;
  roleOther: string;
  email: string;
  phone: string;
  notes: string;
  siteId: string; // "" = the whole account
  linkedCustomerId: string; // "" = not an ELE customer
  doNotChase: boolean;
  doNotChaseHard: boolean;
}

const EMPTY_FORM: FormState = { name: "", role: "site", roleOther: "", email: "", phone: "", notes: "", siteId: "", linkedCustomerId: "", doNotChase: false, doNotChaseHard: false };

function formFrom(c: Contact): FormState {
  return {
    name: c.name,
    role: c.role,
    roleOther: c.roleOther ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    notes: c.notes ?? "",
    siteId: c.siteId ?? "",
    linkedCustomerId: c.linkedCustomer?.id ?? "",
    doNotChase: c.doNotChase === true,
    doNotChaseHard: c.doNotChaseHard === true,
  };
}

export function ContactsPanel({
  customerId,
  siteId,
}: {
  // The account the contacts belong to.
  customerId: string;
  // Set on the site page: list the people at this site, and tie new ones to it.
  siteId?: string;
}) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "office";
  const onSitePage = Boolean(siteId);

  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [agentFor, setAgentFor] = useState<Contact[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [siteOptions, setSiteOptions] = useState<Option[]>([]);

  const [editing, setEditing] = useState<Contact | "new" | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<{ contact: Contact; kind: "archive" | "delete" } | null>(null);
  // Session CR1: confirm / revoke a contact's address as a recognised correspondent (branded two-step, names the address).
  const [corr, setCorr] = useState<{ contact: Contact; kind: "confirm" | "revoke"; expires: string | null } | null>(null);
  const [corrBusy, setCorrBusy] = useState(false);
  const [corrError, setCorrError] = useState<string | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await contactsApi.list(siteId ? { siteId } : { customerId }, showArchived);
      setContacts(list);
      setLoadError(null);
      if (!siteId) setAgentFor(await contactsApi.list({ linkedCustomerId: customerId }));
    } catch (err) {
      setLoadError(friendlyApiError(err, "Could not load the contacts."));
    }
  }, [customerId, siteId, showArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  // The customer page offers a site picker (the account's own sites); the site page ties every new contact to itself.
  useEffect(() => {
    if (onSitePage || !canEdit) return;
    Promise.resolve()
      .then(() => sitesResource.list({ customerId }))
      .then((rows) => setSiteOptions(rows.filter((s) => s.customerId === customerId).map((s) => ({ id: s.id, name: s.name }))))
      .catch(() => setSiteOptions([]));
  }, [customerId, onSitePage, canEdit]);

  const visible = useMemo(() => contacts ?? [], [contacts]);

  function openNew() {
    setForm({ ...EMPTY_FORM, siteId: siteId ?? "", role: siteId ? "site" : "billing" });
    setFormError(null);
    setEditing("new");
  }

  function openEdit(c: Contact) {
    setForm(formFrom(c));
    setFormError(null);
    setEditing(c);
  }

  async function save() {
    if (!form.name.trim()) {
      setFormError("Give the contact a name.");
      return;
    }
    if (form.role === "other" && !form.roleOther.trim()) {
      setFormError("Say what this contact's role is.");
      return;
    }
    const body = {
      name: form.name.trim(),
      role: form.role,
      roleOther: form.role === "other" ? form.roleOther.trim() : null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      notes: form.notes.trim() || null,
      siteId: form.siteId || null,
      linkedCustomerId: form.role === "managing_agent" && form.linkedCustomerId ? form.linkedCustomerId : null,
      doNotChase: form.doNotChase,
      doNotChaseHard: form.doNotChaseHard,
    };
    setSaving(true);
    setFormError(null);
    try {
      if (editing === "new") await contactsApi.create({ customerId, ...body });
      else if (editing) await contactsApi.update(editing.id, body);
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(friendlyApiError(err, "Could not save the contact. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  async function runConfirm() {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      if (confirm.kind === "archive") await contactsApi.archive(confirm.contact.id);
      else await contactsApi.deletePermanently(confirm.contact.id);
      setConfirm(null);
      await load();
    } catch (err) {
      setConfirmError(friendlyApiError(err, "That did not work. Please try again."));
    } finally {
      setConfirmBusy(false);
    }
  }

  async function openCorr(c: Contact, kind: "confirm" | "revoke") {
    setRowError(null);
    setCorrError(null);
    if (kind === "revoke") {
      setCorr({ contact: c, kind, expires: c.correspondentExpiresAt ?? null });
      return;
    }
    try {
      const p = await contactsApi.confirmCorrespondentPreview(c.id);
      setCorr({ contact: c, kind, expires: p.wouldExpireAt });
    } catch (err) {
      setRowError(friendlyApiError(err, "Could not check that contact. Please try again."));
    }
  }

  async function runCorr() {
    if (!corr) return;
    setCorrBusy(true);
    setCorrError(null);
    try {
      if (corr.kind === "confirm") await contactsApi.confirmCorrespondent(corr.contact.id);
      else await contactsApi.revokeCorrespondent(corr.contact.id);
      setCorr(null);
      await load();
    } catch (err) {
      setCorrError(friendlyApiError(err, "That did not work. Please try again."));
    } finally {
      setCorrBusy(false);
    }
  }

  async function restore(c: Contact) {
    setRowError(null);
    try {
      await contactsApi.restore(c.id);
      await load();
    } catch (err) {
      setRowError(friendlyApiError(err, "Could not restore the contact. Please try again."));
    }
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label={onSitePage ? "Contacts at this site" : "Named contacts"}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg text-ele-blue">{onSitePage ? "Contacts at this site" : "Named contacts"}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 font-menu text-xs tracking-wide text-ele-body">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived
          </label>
          {canEdit && (
            <button type="button" onClick={openNew} className={smallBtn}>
              Add contact
            </button>
          )}
        </div>
      </div>
      <p className="mb-3 text-xs text-ele-body">
        {onSitePage
          ? "The people at this building, such as the on-site or facilities contact, the managing agent and an out-of-hours keyholder."
          : "The people on this account and what each one is for. A contact tied to a site says which."}{" "}
        For the office to use by hand: nothing is emailed to these people automatically.
      </p>

      {loadError && (
        <p role="alert" className="text-sm text-ele-danger">
          {loadError}
        </p>
      )}
      {rowError && (
        <p role="alert" className="mb-2 text-sm text-ele-danger">
          {rowError}
        </p>
      )}
      {!loadError && contacts === null && <Loader shape="inline" />}
      {contacts !== null && visible.length === 0 && <p className="text-sm text-ele-body">No contacts recorded yet.</p>}

      {visible.length > 0 && (
        <ul className="divide-y divide-ele-purple-accent">
          {visible.map((c) => (
            <li key={c.id} className={`flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between ${c.archived ? "opacity-60" : ""}`} data-testid="contact-row">
              <div className="min-w-0 text-sm">
                <p className="text-ele-strong">
                  <span className="font-semibold">{c.name}</span>
                  <span className="ml-2 rounded-ele border border-ele-purple-accent px-2 py-0.5 font-menu text-xs tracking-wide text-ele-body">{c.roleLabel}</span>
                  {c.archived && <span className="ml-2 font-menu text-xs tracking-wide text-ele-body">Archived</span>}
                </p>
                {!onSitePage && <p className="text-xs text-ele-body">{c.siteName ? `At ${c.siteName}` : "Whole account"}</p>}
                {c.linkedCustomer && (
                  <p className="text-xs text-ele-body">
                    Managing agent:{" "}
                    <Link to={`/office/customers/${encodeURIComponent(c.linkedCustomer.id)}`} className="text-ele-green underline-offset-2 hover:underline">
                      {c.linkedCustomer.name}
                    </Link>
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-ele-strong">
                  {c.email && <ContactValue value={c.email} kind="email" />}
                  {c.phone && <ContactValue value={c.phone} kind="phone" />}
                  {!c.email && !c.phone && <span className="text-ele-body">No email or phone on record</span>}
                </div>
                {c.notes && <p className="mt-1 whitespace-pre-line text-xs text-ele-body">{c.notes}</p>}
                {c.confirmedCorrespondent && c.correspondentExpiresAt && (
                  <p className="mt-1 text-xs text-ele-body" data-testid="correspondent-note">
                    Confirmed correspondent until {formatDateLong(c.correspondentExpiresAt)}
                  </p>
                )}
                {(c.doNotChase || c.doNotChaseHard) && (
                  <p className="mt-1 text-xs text-ele-body">{c.doNotChase ? "Never chased" : "Only ever chased softly"}</p>
                )}
              </div>
              {canEdit && (
                <div className="flex shrink-0 flex-wrap gap-2">
                  {c.archived ? (
                    <>
                      <button type="button" onClick={() => void restore(c)} className={smallBtn}>
                        Restore
                      </button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setConfirmError(null);
                          setConfirm({ contact: c, kind: "delete" });
                        }}
                      >
                        Delete permanently
                      </Button>
                    </>
                  ) : (
                    <>
                      {c.email && (CORRESPONDENT_ROLES as readonly string[]).includes(c.role) && (
                        <button
                          type="button"
                          onClick={() => void openCorr(c, c.confirmedCorrespondent ? "revoke" : "confirm")}
                          className={smallBtn}
                          aria-label={`${c.confirmedCorrespondent ? "Revoke correspondent for" : "Confirm correspondent"} ${c.name}`}
                        >
                          {c.confirmedCorrespondent ? "Revoke correspondent" : "Confirm correspondent"}
                        </button>
                      )}
                      <button type="button" onClick={() => openEdit(c)} className={smallBtn} aria-label={`Edit ${c.name}`}>
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmError(null);
                          setConfirm({ contact: c, kind: "archive" });
                        }}
                        className={smallBtn}
                        aria-label={`Remove ${c.name}`}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!onSitePage && agentFor.length > 0 && (
        <div className="mt-4 border-t border-ele-purple-accent pt-4">
          <h3 className="mb-2 font-heading text-base text-ele-blue">Managing agent for</h3>
          <p className="mb-2 text-xs text-ele-body">Accounts whose contacts name this customer as their managing agent.</p>
          <ul className="space-y-1 text-sm">
            {agentFor.map((c) => (
              <li key={c.id} className="text-ele-strong">
                <Link to={`/office/customers/${encodeURIComponent(c.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
                  {c.customerName ?? "A customer"}
                </Link>
                {c.siteName ? ` · ${c.siteName}` : ""} <span className="text-ele-body">({c.name})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <ModalShell ariaLabel={editing === "new" ? "Add contact" : "Edit contact"} onClose={() => !saving && setEditing(null)} width="max-w-md">
          <h2 className="mb-4 font-heading text-xl text-ele-strong">{editing === "new" ? "Add contact" : "Edit contact"}</h2>
          <div className="space-y-3">
            <div>
              <label className={labelCls} htmlFor="contact-name">
                Name
              </label>
              <input id="contact-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="contact-role">
                Role
              </label>
              <select id="contact-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as ContactRole }))} className={inputCls}>
                {CONTACT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {CONTACT_ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            {form.role === "other" && (
              <div>
                <label className={labelCls} htmlFor="contact-role-other">
                  What is their role?
                </label>
                <input id="contact-role-other" value={form.roleOther} onChange={(e) => setForm((f) => ({ ...f, roleOther: e.target.value }))} className={inputCls} />
              </div>
            )}
            {form.role === "managing_agent" && (
              <div>
                <label className={labelCls} htmlFor="contact-agent">
                  Is the managing agent one of our customers?
                </label>
                <RecordPicker
                  kind="customer"
                  id="contact-agent"
                  value={form.linkedCustomerId}
                  placeholder="No, not a customer (search to link one)"
                  selectedLabel={editing && editing !== "new" ? editing.linkedCustomer?.name : undefined}
                  onChange={(id) => {
                    if (id && id === customerId) {
                      setFormError("A customer cannot be its own managing agent.");
                      return;
                    }
                    setForm((f) => ({ ...f, linkedCustomerId: id }));
                  }}
                />
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="contact-email">
                Email
              </label>
              <input id="contact-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="contact-phone">
                Phone
              </label>
              <input id="contact-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
            </div>
            {!onSitePage && (
              <div>
                <label className={labelCls} htmlFor="contact-site">
                  Site
                </label>
                <select id="contact-site" value={form.siteId} onChange={(e) => setForm((f) => ({ ...f, siteId: e.target.value }))} className={inputCls}>
                  <option value="">Whole account</option>
                  {siteOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="contact-notes">
                Notes
              </label>
              <textarea id="contact-notes" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
            </div>
            {/* Session AUH (Phase 7): the two chase flags for this person. */}
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-5">
              <label className="flex min-h-11 items-center gap-2 text-sm text-ele-strong sm:min-h-0">
                <input type="checkbox" checked={form.doNotChase} onChange={(e) => setForm((f) => ({ ...f, doNotChase: e.target.checked }))} className="h-4 w-4 accent-ele-green" />
                Never chase this person
              </label>
              <label className="flex min-h-11 items-center gap-2 text-sm text-ele-strong sm:min-h-0">
                <input type="checkbox" checked={form.doNotChaseHard} onChange={(e) => setForm((f) => ({ ...f, doNotChaseHard: e.target.checked }))} className="h-4 w-4 accent-ele-green" />
                Only ever chase them softly
              </label>
            </div>
          </div>
          {formError && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
              {formError}
            </p>
          )}
          <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={() => setEditing(null)}
              disabled={saving}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              Cancel
            </button>
            <Button size="sm" onClick={() => void save()} disabled={saving} className={MODAL_ACTION_BTN}>
              {saving ? "Saving..." : editing === "new" ? "Add contact" : "Save changes"}
            </Button>
          </div>
        </ModalShell>
      )}

      {corr && (
        <ConfirmDeleteModal
          open
          variant="archive"
          title={corr.kind === "confirm" ? `Confirm ${corr.contact.email} as a correspondent?` : `Revoke ${corr.contact.email} as a correspondent?`}
          message={
            corr.kind === "confirm"
              ? `Emails from exactly ${corr.contact.email} will be answered from ${corr.contact.customerName ?? "this customer"}'s records instead of being held back as an unrecorded sender${corr.expires ? `, until ${formatDateLong(corr.expires)}` : ""}. It does not choose who a reply goes to, and every reply is still a Gmail draft a person sends. The platform does not check who really sent an email, so confirm only an address you know belongs to this person.`
              : `Emails from ${corr.contact.email} will be held back as an unrecorded sender again.`
          }
          confirmLabel={corr.kind === "confirm" ? "Confirm correspondent" : "Revoke"}
          busy={corrBusy}
          error={corrError}
          onConfirm={() => void runCorr()}
          onCancel={() => !corrBusy && setCorr(null)}
        />
      )}

      {confirm && (
        <ConfirmDeleteModal
          open
          variant={confirm.kind === "archive" ? "archive" : "destructive"}
          title={confirm.kind === "archive" ? `Remove ${confirm.contact.name}?` : `Delete ${confirm.contact.name} permanently?`}
          message={
            confirm.kind === "archive"
              ? `This archives the contact: it leaves this list and can be restored from Show archived.${confirm.contact.confirmedCorrespondent ? " Its confirmation as a correspondent is withdrawn, and restoring the contact does not bring it back." : ""}`
              : "This deletes the contact and their details for good, for example when a person asks for their details to be erased. It cannot be undone."
          }
          confirmLabel={confirm.kind === "archive" ? "Remove" : "Delete permanently"}
          busy={confirmBusy}
          error={confirmError}
          onConfirm={() => void runConfirm()}
          onCancel={() => !confirmBusy && setConfirm(null)}
        />
      )}
    </section>
  );
}
